import { Link, useLocation } from 'react-router-dom';
import { Heart, Menu, X, LogIn } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const PublicLayout = ({ children, showNav = true }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/programme', label: 'Programme' },
    { path: '/hebergements', label: 'Hébergement' },
    { path: '/actualites', label: 'Actualités' },
    { path: '/photos', label: 'Photos' },
    { path: '/faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen bg-bone relative">
      {/* Desktop Navigation */}
      {showNav && (
        <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 bg-bone/80 backdrop-blur-md border-b border-stone-light">
          <div className="max-w-6xl mx-auto w-full px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-charcoal hover:text-terracotta transition-colors">
              <Heart className="w-5 h-5 text-terracotta" fill="#D4A373" />
              <span className="font-display text-lg">M & C</span>
            </Link>
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-terracotta'
                      : 'text-stone hover:text-charcoal'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/espace-invite"
                className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-full text-sm font-medium hover:bg-terracotta/90 transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Connexion
              </Link>
            </div>
          </div>
        </nav>
      )}

      {/* Mobile Navigation */}
      {showNav && (
        <>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg"
          >
            {menuOpen ? <X className="w-6 h-6 text-charcoal" /> : <Menu className="w-6 h-6 text-charcoal" />}
          </button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="md:hidden fixed inset-0 z-40 bg-bone flex flex-col items-center justify-center gap-6"
              >
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMenuOpen(false)}
                    className={`text-2xl font-display transition-colors ${
                      location.pathname === link.path
                        ? 'text-terracotta'
                        : 'text-charcoal hover:text-terracotta'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/espace-invite"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 mt-4 px-6 py-3 bg-terracotta text-white rounded-full text-lg font-medium"
                >
                  <LogIn className="w-5 h-5" />
                  Connexion
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Main Content */}
      <main className={showNav ? 'md:pt-16' : ''}>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-light py-8 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-terracotta" fill="#D4A373" />
            <span className="font-display text-lg text-charcoal">Mélanie & Cédric</span>
            <Heart className="w-4 h-4 text-terracotta" fill="#D4A373" />
          </div>
          <p className="text-sm text-stone mb-2">5 septembre 2026</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/confidentialite" className="text-xs text-stone hover:text-terracotta transition-colors">
              Politique de confidentialité
            </Link>
            <span className="text-stone">•</span>
            <Link to="/admin" className="text-xs text-stone hover:text-terracotta transition-colors">
              Administration
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const AdminLayout = ({ children, title }) => {
  const location = useLocation();

  const navLinks = [
    { path: '/admin/dashboard', label: 'Tableau de bord' },
    { path: '/admin/invites', label: 'Invités' },
    { path: '/admin/liste-invites', label: 'Liste autorisée' },
    { path: '/admin/brunch', label: 'Brunch' },
    { path: '/admin/hebergements', label: 'Hébergements' },
    { path: '/admin/paiements', label: 'Paiements' },
    { path: '/admin/relances', label: 'Relances' },
    { path: '/admin/notifications', label: 'Notifications' },
    { path: '/admin/programme', label: 'Programme' },
    { path: '/admin/faq', label: 'FAQ' },
    { path: '/admin/livre-dor', label: 'Livre d\'Or' },
    { path: '/admin/questions', label: 'Questions' },
    { path: '/admin/blog', label: 'Blog' },
    { path: '/admin/photos', label: 'Photos' },
    { path: '/admin/delivrabilite', label: 'Délivrabilité' },
    { path: '/admin/configuration', label: 'Configuration' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-terracotta" fill="#D4A373" />
                <span className="font-display text-xl text-charcoal">Admin</span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-stone hover:text-charcoal transition-colors">
                Voir le site
              </Link>
              <button
                data-testid="admin-logout-btn"
                onClick={handleLogout}
                className="text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Navigation */}
      <nav className="bg-white border-b border-gray-200 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  location.pathname === link.path
                    ? 'border-terracotta text-terracotta'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Page Title */}
      {title && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
            <h1 className="text-2xl font-display text-charcoal">{title}</h1>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>
    </div>
  );
};
